import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react'
import { useLiveQuery } from 'dexie-react-hooks'
import { isNil } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { db } from '../../data/dexie/Database'
import type { S3PBrowserFilter, S3PBrowserProps } from '../../types/props'

export const S3PBrowser = observer(({ matchId, isOpen, setIsOpen }: S3PBrowserProps) => {
  const [filter, setFilter] = useState<S3PBrowserFilter>('')
  const [filterValue, setFilterValue] = useState('')
  const [schemaJson, setSchemaJson] = useState<string>('')
  const [isGenerating, setIsGenerating] = useState(false)
  const toast = useToast()

  const data = useLiveQuery(
    () =>
      db.s3p
        .where({ matchId: matchId })
        .filter(item => {
          const pd = JSON.parse(item.data)
          const filterSplit = filterValue.split('.')
          return (
            pd.payload.type !== 'Event.Sport.Cricket.Heartbeat' &&
            (filterValue === '' ||
              (filter == 'id' && pd.payload.id.indexOf(filterValue) > -1) ||
              (filter == 'overBall' &&
                !isNil(pd.payload.over_number) &&
                (pd.payload.over_number - 1).toString().indexOf(filterSplit[0]) > -1 &&
                !isNil(pd.payload.ball_number) &&
                (filterSplit.length === 1 || pd.payload.ball_number.toString().indexOf(filterSplit[1]) > -1)))
          )
        })
        .reverse()
        .limit(filterValue === '' ? 50 : 1000)
        .toArray(),
    [filter, filterValue]
  )

  const generateSchema = async () => {
    setIsGenerating(true)
    try {
      const allMessages = await db.s3p
        .where({ matchId: matchId })
        .filter(item => {
          const pd = JSON.parse(item.data)
          return pd.payload.type !== 'Event.Sport.Cricket.Heartbeat'
        })
        .toArray()

      const schema: any = {}

      const mergeSchema = (target: any, source: any, path = '') => {
        for (const key in source) {
          const value = source[key]
          const currentPath = path ? `${path}.${key}` : key

          if (value === null || value === undefined) {
            if (!(key in target)) {
              target[key] = value
            }
          } else if (typeof value === 'object' && !Array.isArray(value)) {
            if (!(key in target)) {
              target[key] = {}
            }
            mergeSchema(target[key], value, currentPath)
          } else if (Array.isArray(value)) {
            if (!(key in target)) {
              target[key] = []
            }
            if (value.length > 0 && typeof value[0] === 'object' && !Array.isArray(value[0])) {
              const arraySchema = {}
              value.forEach(item => mergeSchema(arraySchema, item, currentPath))
              target[key] = [arraySchema]
            } else if (value.length > 0) {
              target[key] = [value[0]]
            }
          } else {
            if (!(key in target)) {
              target[key] = value
            }
          }
        }
      }

      allMessages.forEach(item => {
        const parsedItem = JSON.parse(item.data)
        mergeSchema(schema, parsedItem)
      })

      const schemaString = JSON.stringify(schema, null, 2)
      setSchemaJson(schemaString)

      toast({
        title: 'Schema generated',
        description: `Processed ${allMessages.length} messages`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: 'Error generating schema',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsGenerating(false)
    }
  }

  const downloadSchema = () => {
    const blob = new Blob([schemaJson], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `s3p-schema-${matchId}-${new Date().toISOString()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} scrollBehavior="inside" size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          S3P Payloads
          <HStack spacing="14px" pt="7px">
            <Input
              placeholder="Filter by over.ball"
              value={filter === 'overBall' ? filterValue : ''}
              onChange={evt => {
                setFilter('overBall')
                setFilterValue(evt.target.value)
              }}
            />
            <Text fontSize="md">or</Text>
            <Input
              placeholder="Filter by payload.id"
              value={filter === 'id' ? filterValue : ''}
              onChange={evt => {
                setFilter('id')
                setFilterValue(evt.target.value)
              }}
            />
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Accordion allowToggle mb="14px">
            <AccordionItem>
              <h2>
                <AccordionButton>
                  <Box flex="1" textAlign="left" fontWeight="bold">
                    Schema Generator
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <Flex direction="column" gap="14px">
                  <Text fontSize="sm" color="gray.600">
                    Scan all S3P messages to generate a unified JSON schema showing all unique keys with example values.
                  </Text>
                  <HStack>
                    <Button
                      colorScheme="blue"
                      onClick={generateSchema}
                      isLoading={isGenerating}
                      loadingText="Generating..."
                    >
                      Generate Schema
                    </Button>
                    {schemaJson && (
                      <Button colorScheme="green" onClick={downloadSchema}>
                        Download JSON
                      </Button>
                    )}
                  </HStack>
                  {schemaJson && (
                    <Box background="gray.100" padding="14px" borderRadius="md" maxH="400px" overflowY="auto">
                      <Text as="pre" fontSize="12px" whiteSpace="pre-wrap">
                        {schemaJson}
                      </Text>
                    </Box>
                  )}
                </Flex>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
          {filterValue === '' && (
            <Alert status="info">
              <AlertIcon />
              Showing 50 most recent messages. Use filters to find specific messages.
            </Alert>
          )}
          {data && data.length > 0 ? (
            data.map(item => {
              const parsedItem = JSON.parse(item.data)
              return (
                <Flex
                  key={`s3pPayload${item.id}`}
                  direction="column"
                  mb="14px"
                  mt={filterValue === '' ? '14px' : '0px'}
                >
                  <Flex direction="row" justifyContent="space-between" backgroundColor="black" px="7px">
                    <Text flex={1} color="white">
                      {item.timestamp}
                    </Text>
                    <Text flex={0.5} color="cls.textGray" textAlign="center">
                      {!isNil(parsedItem.payload.over_number) && !isNil(parsedItem.payload.ball_number)
                        ? `(Over ${Number(parsedItem.payload.over_number) - 1}.${parsedItem.payload.ball_number})`
                        : ''}
                    </Text>
                    <Text flex={1} color="white" textAlign="right">
                      {parsedItem.payload.type}
                    </Text>
                  </Flex>
                  <Box background="gray.200" padding="7px">
                    <Text as="pre" fontSize="12px" whiteSpace="pre-wrap" overflowX="scroll">
                      {JSON.stringify(parsedItem, null, 4)}
                    </Text>
                  </Box>
                </Flex>
              )
            })
          ) : (
            <Text py="14px">No S3P Payloads</Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
})
